import {
  AbsgArrayHelper,
  AppInsights,
  CacheService,
  EnvironmentConfiguration,
  LogService,
  SessionContextService,
} from '@absg/angular-common';
import { AuthenticationService, ConfigurationService, IdleHandlerService } from '@absg/angular-platform';
import { PracticeLite } from '@absg/api-station-management';
import { AuthenticationHelperService, BaseCacheKeys, EnvironmentService, NimAlertService, PrintingTemplateService, SessionService } from '@absg/nim-client-shared';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CmsService } from 'app/main/shared/services/cms.service';
import { Environments } from 'core.enums/environments';
import { AccessPointService } from 'core.services/access-point/access-point.service';
import { CancelHttpService } from 'core.services/cancel-http.service';
import { StationAuthenticationService } from 'core.services/station-authentication.service';
import { StationInfoService } from 'core.services/station-info.service';
import { DispenseDataService } from 'dispense.root/shared/services/dispense-data.service';
import { BehaviorSubject, combineLatest, from, Observable, of, Subscription } from 'rxjs';
import { catchError, concatMap, delay, map, retryWhen, switchMap, take, tap } from 'rxjs/operators';
import { genericRetryStrategy } from 'shared.root/directives/generic-retry.strategy';
import { ElectronApplicationService } from 'shared.root/services/electron-application.service';
import { EmrService } from 'shared.root/services/emr.service';
import { NIMGoogleTagMangerService } from 'shared.root/services/gtm-tag.service';
import { SubSink } from 'subsink';

interface PracticeVm {
  showPracticeDomain: boolean;
  practiceDomain: string;
  practiceDetails: string;
}

@Component({
  selector: '[nc-authentication]',
  templateUrl: 'authentication.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AuthenticationComponent implements OnDestroy, OnInit {
  isLoggedIn: Observable<boolean>;
  environmentName: string;
  isLoginEnabled$ = this.sessionService.isLoginEnabled$;
  private cmsUrlBackup: string;
  private cmsSubscription: Subscription;
  isSpinnerVisible$ = new BehaviorSubject<boolean>(false);
  userIsAuthenticated: boolean;
  retryCount$ = new BehaviorSubject<number>(0);
  private subSink = new SubSink();
  hostName$: Observable<string>;
  appVersion$: Observable<string>;
  practiceDomain$: Observable<PracticeVm>;
  userFullName$: Observable<string>;

  constructor(
    authenticationHelperService: AuthenticationHelperService,
    private idleHandler: IdleHandlerService,
    private authenticationService: AuthenticationService,
    private stationAuthenticationService: StationAuthenticationService,
    private sessionService: SessionService,
    private environmentService: EnvironmentService,
    private nimAlertService: NimAlertService,
    private cmsService: CmsService,
    private stationInfoService: StationInfoService,
    private environment: EnvironmentConfiguration,
    private sessionContext: SessionContextService,
    private cacheService: CacheService,
    private cancelHttpService: CancelHttpService,
    private emrService: EmrService,
    private loggingService: LogService,
    private cd: ChangeDetectorRef,
    private configurationService: ConfigurationService,
    private electronApplicationService: ElectronApplicationService,
    private printingService: PrintingTemplateService,
    private appInsights: AppInsights,
    private accessPointService: AccessPointService,
    private dispenseDataService: DispenseDataService,
    private gtmNimService: NIMGoogleTagMangerService,
  ) {
    this.isLoggedIn = this.authenticationService.isLoggedIn$; // since we need both true and false values to show we can't async pipe this in an ngIf
    this.subSink.sink = this.isLoggedIn.pipe(
      catchError(e => {
        this.loggingService.logError(e);
        return of(false);
      }))
      .subscribe(loggedIn => {
        if (loggedIn) {
          this.idleHandler.setActionToDoBeforeLogout(
            authenticationHelperService.getLogoutAction().pipe(
              take(1),
              concatMap(() => this.performLogoutTasks()),
            ));
        }
        this.userIsAuthenticated = loggedIn;
        this.cd.markForCheck();
      });

    this.subSink.sink = this.environmentService.autoTimeOutTimeInSeconds.pipe(map((value) => {
      const secondsToMinuteDivisor = 60;
      const timeInMinutesBeforeIdleWarningShowsUp = value / secondsToMinuteDivisor;
      const minutesCountdownShowsUp = 1;
      idleHandler.setDifferentIdleTimeouts(timeInMinutesBeforeIdleWarningShowsUp, minutesCountdownShowsUp);
    })).subscribe();

    this.subSink.sink = this.authenticationService.getInvalidPracticeUser().pipe(
      catchError(e => {
        this.loggingService.logError(e);
        return of(true);
      }),
      concatMap(isAccessDenied => {
        if (isAccessDenied) {
          this.stationInfoService.setAccessDenied(isAccessDenied);
          this.idleHandler.setActionToDoBeforeLogout(of(null));
          console.log('INVALID PRACTICE USER');
          return this.beginLogout();
        }
        return of(void 0);
      }),
    ).subscribe();

    this.environmentName = this.environmentService.currentEnvironment === Environments.prod ?
      '' : `| ${this.firstUpperCase(Environments[this.environmentService.currentEnvironment])}`;

    this.triggerBaseLoadStation();
    this.subSink.sink = this.stationAuthenticationService.cancelEvent$
      .subscribe(() => {
        console.log('enabling login');
        this.sessionService.isLoginEnabled.next(true);
        console.log('setting spinner false');
        this.isSpinnerVisible$.next(false);
      });
    this.subSink.sink = this.stationAuthenticationService.isStartingLogout$.pipe(
      tap(() => this.isSpinnerVisible$.next(true)),
    ).subscribe();

    this.subSink.sink = this.sessionService.fireRetryDomainFetch$.pipe(
      tap(() => {
        this.cmsService.setFailedState(false);
        this.triggerBaseLoadStation();
      }),
    ).subscribe();
  }

  ngOnInit(): void {
    this.hostName$ = this.stationInfoService.hostName;
    this.appVersion$ = this.stationInfoService.appVersion;
    this.practiceDomain$ = this.sessionService.practiceDomain.pipe(
      map(practiceDomain => ({
        showPracticeDomain: !!practiceDomain && practiceDomain.length > 0,
        practiceDomain,
        practiceDetails: this.getPracticeDetails(),
      })));
    this.userFullName$ = this.sessionService.getUserFullName().pipe(
      map(userFullName => this.formatString(userFullName)));

    this.subSink.sink = combineLatest([
      this.sessionContext.getPracticeName().pipe(
        tap(practiceName => this.updateElectronPractice(practiceName))),
      this.authenticationService.identityChanges$.pipe(
        tap(user => {
          let name = 'not logged in';
          if (!!user) {
            name = user.usernameFromToken;
          }
          this.updateElectronUser(name);
        })),
    ])
      .pipe(
        catchError(e => {
          this.loggingService.logError(e);
          return of(false);
        }),
      ).subscribe();

    this.cacheService.set(BaseCacheKeys.environment, this.environmentService.environmentFromConfig);
    this.sessionContext.setApplicationVersion(this.stationInfoService.appVersion.value);
  }

  private updateElectronPractice(practiceName: string): void {
    const ipcRenderer = this.electronApplicationService.getIpcRenderer();
    ipcRenderer?.send('set-practice', practiceName);
  }

  private updateElectronUser(userName: string): void {
    const ipcRenderer = this.electronApplicationService.getIpcRenderer();
    ipcRenderer?.send('set-user', userName);
  }

  private formatString(value: string): string {
    return value || '';
  }

  private loadConfig(): Observable<void> {
    return this.configurationService.loadAndAssignLoginConfiguration().pipe(
      retryWhen(genericRetryStrategy({ maxRetryAttempts: 3, retryCount: this.retryCount$, changeDetectionRef: this.cd })),
      tap(() => {
        this.cd.markForCheck();
      }),
    );
  }

  logIn(): void {
    let i = 0;
    console.log(`login clicked ${i++}`);
    if (!this.sessionService.isLoginEnabled.value) {
      console.log(`login clicked abandoned! loginEnabled = ${this.sessionService.isLoginEnabled.value}`);
      return;
    }

    this.sessionService.isLoginEnabled.next(false);
    console.log(`login clicked ${i++}`);
    this.subSink.sink = this.loadConfig().pipe(
      take(1),
      concatMap(() => {
        console.log('logging in...');
        this.loggingService.logTrace('Auth Component - logIn');
        return this.stationAuthenticationService.redirectToLoginPage();
      }),
    ).subscribe(() => { },
      (error) => {
        console.error(error);
        this.sessionService.isLoginEnabled.next(true);
        this.loggingService.logError(error);
        this.nimAlertService.showErrorAlert('Error', new Error('loadAndAssignLoginConfiguration : Error Loading config'));
      });
  }

  logOut(): void {
    const confirmMessage = this.sessionService.getLogoutConfirmationMessage();
    if (confirmMessage) {
      const confirmTitle = this.sessionService.getLogoutConfirmationTitle();
      this.nimAlertService.showConfirmModal(confirmTitle, confirmMessage, '')
        .pipe(take(1),
          concatMap((isAccepted) => {
            if (isAccepted) {
              return this.beginLogout();
            } else {
              return of(void 0);
            }
          })).subscribe();
    } else {
      this.beginLogout().subscribe();
    }
  }

  private beginLogout(): Observable<unknown> {
    console.log('begin logout setting spinner true');
    this.isSpinnerVisible$.next(true);
    return this.stationAuthenticationService.redirectToLogoutPage(true).pipe(
      take(1),
    );
  }

  private getPracticeDetails(): string {
    let practiceDetail = '';
    practiceDetail = (AbsgArrayHelper.isNil(this.sessionService.practiceName.getValue()) ? '' : `${this.sessionService.practiceName.getValue()}, `)
      + (AbsgArrayHelper.isNil(this.sessionService.practiceCity.getValue()) ? '' : `${this.sessionService.practiceCity.getValue()}, `)
      + (AbsgArrayHelper.isNil(this.sessionService.practiceState.getValue()) ? '' : this.sessionService.practiceState.getValue());
    return practiceDetail;
  }

  reloadCMS(): void {
    this.cmsSubscription = combineLatest([this.sessionService.stationName, this.sessionService.practiceDomain])
      .pipe(map(([stationName, practiceDomain]) => ({
        hostname: stationName,
        domain: practiceDomain,
      })))
      .subscribe(x => {
        if (!AbsgArrayHelper.isNil(x.hostname) && !AbsgArrayHelper.isNil(x.domain)) {
          this.cmsUrlBackup = `${this.environment.cmsUrl}?stationname=${x.hostname}&domain=${x.domain}`;
          this.cmsService.setCmsURl(this.cmsUrlBackup);
        }
      });
  }

  ngOnDestroy(): void {
    if (this.cmsSubscription) {
      this.cmsSubscription.unsubscribe();
    }

    this.subSink.unsubscribe();
  }

  performLogoutTasks(): Observable<void> {
    console.log('logging out of station... setting spinner true');
    this.isSpinnerVisible$.next(true);
    this.loggingService.logTrace('Auth Component - performLogoutTasks');
    this.nimAlertService.userLoggedOut$.next();
    this.dispenseDataService.clearFutureMedOrderOptions();
    this.accessPointService.executeShutDownDoorHandling();
    this.cancelHttpService.setLogout(true);
    this.cancelHttpService.cancelPendingRequests();
    this.sessionService.clearCachedLegalDisclaimerData();
    this.sessionService.closeOpenModals();
    this.stationInfoService.clearCachedUserStationAccessData();
    this.authenticationService.setIsUserLoggedInToStation(false);
    this.authenticationService.setInvalidPracticeUser(false);
    this.emrService.clearCachedEmrConfig();
    this.cacheService.clearAllExceptExpired();
    this.nimAlertService.executeLogOut();
    this.gtmNimService.flushData();
    let sessionLogout = this.sessionService.doLogoutWork();
    if (!sessionLogout) {
      sessionLogout = Promise.resolve();
    }
    return from(sessionLogout).pipe(
      take(1),
      delay(2000),// hoping any other http call is done firing
      tap(() => {
        this.cancelHttpService.setLogout(false);
      }),
    );
  }

  private firstUpperCase(strValue: string): string {
    return strValue.charAt(0).toUpperCase() + strValue.slice(1);
  }
  private triggerBaseLoadStation() {
    this.sessionService.isLoginEnabled.next(false);
    this.subSink.sink = combineLatest([
      this.stationInfoService.hostName,
      this.loadConfig(),
    ]).pipe(catchError(e => {
      this.cmsService.setFailedState(true);
      this.loggingService.logError(e);
      return of([null, null]);
    })).subscribe(([hostValue, __]) => {
      if (hostValue && hostValue !== null) {
        this.sessionService.getPracticeInfo(hostValue)
          .pipe(
            retryWhen(genericRetryStrategy({ maxRetryAttempts: 3, retryCount: this.retryCount$, changeDetectionRef: this.cd })),
            catchError(() => of(null)),
            tap(() => {
              this.cd.markForCheck();
            }),
            switchMap((result) => {
              if (result) {
                const practiceLite = this.sessionService.practiceLite.value;
                return this.printingService.getTemplates(practiceLite).pipe(
                  take(1),
                  catchError((error) => {
                    this.loggingService.logError(error);
                    this.nimAlertService.showErrorAlert('Error', new Error('loadAndAssignLoginConfiguration : Error Loading config'));
                    return of(result);
                  }),
                  map(() => result),
                );
              } else {
                return of(result);
              }
            }),
            take(1),
          ).subscribe(value => {
            this.retryCount$.next(0);
            if (value && value !== null) {
              const result = value as PracticeLite;
              this.sessionService.stationName.next(this.stationInfoService.hostName.getValue());
              this.sessionContext.setStationName(hostValue);
              this.sessionService.practiceName.next(result.practiceName);
              this.sessionContext.setPracticeName(result.practiceName);
              this.sessionService.practiceCity.next(result.city);
              this.sessionService.practiceState.next(result.state);
              this.stationInfoService.stationId.next(result.stationId);
              this.authenticationService.setStationPracticeId(result.practiceId);
              this.authenticationService.setStationId(result.stationId);
              this.cmsService.setFailedState(false);
              this.appInsights.setPracticeId(result.practiceId);
              this.appInsights.setPracticeDomain(result.practiceName);
              this.sessionService.isLoginEnabled.next(true);
              this.sessionService.practiceDomain.next(result.domain);
              this.sessionContext.setDomain(result.domain);
            } else {
              this.cmsService.setFailedState(true);
              this.sessionService.isLoginEnabled.next(false);
            }
          });
      }
    });
  }
}
